import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import souvenirService, { SouvenirOrder } from '../../services/souvenir.service'
import LoadingSpinner from '../../components/LoadingSpinner'
import { EP_STATUS_LABELS } from '../../constants/easyparcel-status'

const STATUS_COLORS: Record<string, string> = {
  PENDING: 'bg-yellow-100 text-yellow-800',
  CONFIRMED: 'bg-blue-100 text-blue-800',
  PREPARING: 'bg-indigo-100 text-indigo-800',
  READY: 'bg-purple-100 text-purple-800',
  SHIPPED: 'bg-cyan-100 text-cyan-800',
  COMPLETED: 'bg-green-100 text-green-800',
  CANCELLED: 'bg-red-100 text-red-800',
}

const MyOrdersPage: React.FC = () => {
  const { t } = useTranslation()
  const [orders, setOrders] = useState<SouvenirOrder[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const load = async () => {
      try {
        const res = await souvenirService.getMyOrders()
        // Response shape: { success, data: [...] } or plain array
        const list = Array.isArray(res.data) ? res.data : res.data?.data || []
        setOrders(list)
      } catch (err) {
        console.error('Error loading orders:', err)
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [])

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner />
      </div>
    )
  }

  if (!orders.length) {
    return (
      <div className="max-w-3xl mx-auto text-center py-16">
        <h2 className="text-xl font-semibold text-gray-900">{t('orders.noOrders')}</h2>
        <Link
          to="/products"
          className="mt-6 inline-block px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
        >
          {t('cart.continueShopping')}
        </Link>
      </div>
    )
  }

  return (
    <div className="max-w-5xl mx-auto">
      <h1 className="text-2xl font-bold text-gray-900 mb-6">{t('orders.myOrders')}</h1>

      <div className="space-y-4">
        {orders.map((order) => {
          const total = Number(order.totalAmount)
          return (
            <Link
              key={order.id}
              to={`/my-orders/${order.id}`}
              className="block bg-white shadow rounded-lg p-5 hover:shadow-md transition"
            >
              <div className="flex items-center justify-between gap-4 flex-wrap">
                <div>
                  <p className="font-semibold text-gray-900">{order.orderNumber}</p>
                  <p className="text-sm text-gray-500">
                    {new Date(order.createdAt).toLocaleDateString()} ·{' '}
                    {order.items.length} {t('orders.items')}
                  </p>
                  {order.deliveryMethod === 'DELIVERY' && order.courierName && (
                    <p className="text-sm text-gray-500">
                      {order.courierName} · {order.serviceName}
                    </p>
                  )}
                  {order.deliveryMethod === 'PICKUP' && (
                    <p className="text-sm text-gray-500">{t('checkout.pickup')}</p>
                  )}
                </div>

                <div className="flex items-center gap-3">
                  {order.epStatus && order.status === 'SHIPPED' && (
                    <span className="px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                      {EP_STATUS_LABELS[order.epStatus] || `EP ${order.epStatus}`}
                    </span>
                  )}
                  <span
                    className={`px-3 py-1 rounded-full text-xs font-medium ${
                      STATUS_COLORS[order.status] || 'bg-gray-100 text-gray-800'
                    }`}
                  >
                    {t(`orders.status.${order.status.toLowerCase()}`, order.status)}
                  </span>
                  <span className="font-bold text-gray-900">RM{total.toFixed(2)}</span>
                </div>
              </div>

              {/* Item thumbnails */}
              <div className="mt-3 flex gap-2">
                {order.items.slice(0, 5).map((item) => (
                  <div key={item.id} className="flex items-center gap-2 text-xs text-gray-500">
                    {item.images?.[0] ? (
                      <img
                        src={item.images[0]}
                        alt={item.name}
                        className="h-10 w-10 object-cover rounded"
                      />
                    ) : (
                      <div className="h-10 w-10 bg-gray-200 rounded" />
                    )}
                  </div>
                ))}
                {order.items.length > 5 && (
                  <span className="text-xs text-gray-400 self-center">
                    +{order.items.length - 5}
                  </span>
                )}
              </div>
            </Link>
          )
        })}
      </div>
    </div>
  )
}

export default MyOrdersPage
